import { Spinner, SpinnerSize } from '@fluentui/react';
import { useEffect, useState } from 'react';
import { extractLocale } from 'src/common/utilities/Utilities';
import localeService from 'src/dataServices/LocaleService';
import localizationService from 'src/dataServices/LocalizationService';
import './LocalePicker.scss';

interface ILocalePicker {
  supportedRegions: string[];
  locale: string;
}

const LOCALE_PICKER_RETURN_PATH_KEY = 'localePickerReturnPath';

export const LocalePicker = (props: ILocalePicker) => {
  const languages = localizationService.getSupportedLanguages();
  const [UFHLoading, setUHFLoading] = useState(true);
  const [storedPath, setStoredPath] = useState('/');
  const normalizedLocale = props.locale.toLowerCase();
  const isValidSupportedRegion = props.supportedRegions.some(
    (region) => region.toLowerCase() === normalizedLocale
  );
  const isValidSupportedLanguage = languages.some(
    (language) => language.locale.toLowerCase() === normalizedLocale
  );

  if (!isValidSupportedRegion && !isValidSupportedLanguage) {
    window.location.replace('/');
  }

  const getPathWithoutLocale = (path: string): string => {
    const localeFromPath = extractLocale(path);
    if (!localeFromPath) {
      return path;
    }

    const localizedPrefix = `/${localeFromPath}`;
    if (!path.toLowerCase().startsWith(localizedPrefix.toLowerCase())) {
      return path;
    }

    const pathWithoutLocale = path.slice(localizedPrefix.length);
    return pathWithoutLocale.length > 0 ? pathWithoutLocale : '/';
  };

  const getLocaleLink = (targetLocale: string): string => {
    const pathWithoutLocale = getPathWithoutLocale(storedPath);
    return `/${targetLocale}${pathWithoutLocale}`;
  };

  // runs UHF scripts on mount for getting started page
  useEffect(() => {
    window.dispatchEvent(new Event('loadUHF'));

    const returnTo = new URLSearchParams(window.location.search).get('returnTo');
    try {
      if (returnTo?.startsWith('/')) {
        sessionStorage.setItem(LOCALE_PICKER_RETURN_PATH_KEY, returnTo);
      } else if (document.referrer) {
        const referrerUrl = new URL(document.referrer);
        const referrerPath = referrerUrl.pathname;
        const isSameOrigin = referrerUrl.origin === window.location.origin;
        const isLocalePage = referrerPath.toLowerCase().endsWith('/locale');

        if (isSameOrigin && !isLocalePage) {
          sessionStorage.setItem(LOCALE_PICKER_RETURN_PATH_KEY, referrerPath);
        }
      }

      const path = sessionStorage.getItem(LOCALE_PICKER_RETURN_PATH_KEY);
      if (path) {
        setStoredPath(path);
      }
    } catch {
      // Handle storage/security issues and keep a safe fallback.
      setStoredPath('/');
    }

    const removeFocusableHeadings = () => {
      const rogue = document.querySelectorAll('.c-uhf-sronly');
      rogue.forEach((el) => {
        el.removeAttribute('tabIndex');
        el.setAttribute('tabIndex', '-1');
      });
    };

    const onUHFDoneLoading = () => {
      setUHFLoading(false);
    };

    window.addEventListener('UHFDoneLoading', removeFocusableHeadings, { once: true });
    window.addEventListener('UHFDoneLoading', onUHFDoneLoading, false);

    return () => {
      window.removeEventListener('UHFDoneLoading', removeFocusableHeadings);
      window.removeEventListener('UHFDoneLoading', onUHFDoneLoading);
    };
  }, []);

  if (UFHLoading) {
    return (
      <Spinner
        size={SpinnerSize.large}
        label={localeService.getText('Shared', 'SpinnerPleaseWait')}
        ariaLive="assertive"
      />
    );
  }

  return (
    <div className="locale-picker-container">
      <h1 className="locale-header">{localeService.getText('LocalizationPicker', 'Header')}</h1>
      {languages.map((language) => (
        <p>
          <a className="locale-link" href={getLocaleLink(language.locale)}>
            {`${language.country} - ${language.language}`}
          </a>
        </p>
      ))}
    </div>
  );
};

export default LocalePicker;
